import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons'
import { faClockRotateLeft, faFileInvoice, faShuffle, faTrash, faUser } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReservationHeaderComponentsCommonProps } from './header'
import ReferenceFormDialog from './reference-form/reference-form.dialog'
import ReservationTypeFormDialog from './reservation-type-form/reservation-type-form.dialog'
import BillsDataFormDialog from './bills-data-form/bills-data-form.dialog'
import { ReservedResourcePhase } from 'src/api/api.generated'
import { HiCIcon } from 'src/app/components/icons/icon'
import ReservationAuditDrawer from './reservation-audit/reservation-audit.drawer'
import { useProperty } from 'src/app/hooks/context/context.hook'
import { getDescription } from './header-components'

export function HeaderActionMenu(props: ReservationHeaderComponentsCommonProps & { onDelete: () => void; productionIds: string[]; billIds: string[]; documentIds: string[] }) {
  const { t } = useTranslation()
  const property = useProperty()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const [openReferenceForm, setOpenReferenceForm] = useState(false)
  const [openReservationTypeForm, setOpenReservationTypeForm] = useState(false)
  const [openBillingDataForm, setOpenBillingDataForm] = useState(false)
  const [openReservationAudit, setOpenReservationAudit] = useState(false)

  const showDelete = useMemo(() => {
    return (props.reservation.reservedResources ?? []).every((rr) => rr.phase === ReservedResourcePhase.RESERVATION)
  }, [props.reservation])

  return (
    <>
      <IconButton disabled={props.readOnly} color="primary" sx={{ fontSize: '1.25rem', padding: 0.5 }} onClick={(event) => setAnchorEl(event.currentTarget)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </IconButton>
      <Menu //
        open={anchorEl ? true : false}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setOpenReferenceForm(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faUser} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.booking.reservation.header.menu.customer')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenReservationTypeForm(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faShuffle} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.booking.reservation.header.menu.reservation')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenBillingDataForm(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faFileInvoice} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.booking.reservation.header.menu.bills')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenReservationAudit(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.booking.reservation.header.menu.audit')}</ListItemText>
        </MenuItem>
        {showDelete && (
          <MenuItem onClick={props.onDelete}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrash} style={{ color: 'var(--error)' }} />
            </ListItemIcon>
            <ListItemText sx={{ '& > *': { color: 'var(--error)!important' } }}>{t('pms.booking.reservation.header.menu.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {openReferenceForm && (
        <ReferenceFormDialog
          reservation={props.reservation}
          open={openReferenceForm}
          onClose={() => {
            setOpenReferenceForm(false)
          }}
        />
      )}
      {openReservationTypeForm && (
        <ReservationTypeFormDialog
          reservation={props.reservation}
          open={openReservationTypeForm}
          onClose={() => {
            setOpenReservationTypeForm(false)
          }}
        />
      )}
      {openBillingDataForm && (
        <BillsDataFormDialog
          reservation={props.reservation}
          open={openBillingDataForm}
          onClose={() => {
            setOpenBillingDataForm(false)
          }}
        />
      )}
      {openReservationAudit && (
        <ReservationAuditDrawer
          open={openReservationAudit}
          productionIds={props.productionIds}
          tenantId={property?.tenantId ?? ''}
          propertyId={property?.id ?? ''}
          reservedResourceIds={props.reservation.reservedResources?.map((reservedResource) => reservedResource.id) ?? []}
          reservationId={props.reservation.id}
          billIds={props.billIds}
          documentIds={props.documentIds}
          reservationCode={props.reservation.reservationCode}
          description={getDescription(props.reservation)}
          onClose={() => {
            setOpenReservationAudit(false)
          }}
        />
      )}
    </>
  )
}
